import {Component, ElementRef, EventEmitter, Inject, Input, OnChanges, OnInit, Output, Renderer2, ViewChild, ViewEncapsulation} from '@angular/core';
import {GridIdentifier} from '@application/grids/grid-identifier.enum';
import {DraggingService} from '@application/services/dragging/dragging.service';
import {OrderLinesTarget} from '@domain/order-lines-target.enum';
import {ProductionOrderCustomSettings} from '@domain/production-schedule/custom-settings/production-order-custom-settings';
import {ProductionSchedule} from '@domain/production-schedule/production-schedule';
import {ProductionScheduleDrawingViewMode} from '@domain/production-schedule/production-schedule-drawing-view-mode.enum';
import {ProductionScheduleItemInPathGroup} from '@domain/production-schedule/production-schedule-item-in-path-group';
import {ProductionSchedulePath} from '@domain/production-schedule/production-schedule-path';
import {ProductionSchedulePathsOfColoredYarnSet} from '@domain/production-schedule/production-schedule-paths-of-colored-yarn-set';
import {Permission} from '@domain/profile/permission.enum';
import {Subscription} from '@domain/profile/subscription';
import {ColorSet} from '@domain/textile-data/color-set/color-set';
import {ColoredYarnSet} from '@domain/textile-data/colored-yarn-set/colored-yarn-set';
import {OverviewListColoredYarnSetWithStartDent} from '@domain/textile-data/creel/overview-list-colored-yarn-set-with-start-dent';
import {CarpetWeaveQuality} from '@domain/textile-data/machine-quality/carpet-weave-quality';
import {PathWidth} from '@domain/textile-data/machine-quality/path-width';
import {WeaveQuality} from '@domain/textile-data/machine-quality/weave-quality';
import {StatusBarTarget} from '@domain/textile-data/status-bar-target.constants';
import {AUTHENTICATION, Authentication} from '@infrastructure/http/authentication/authentication';
import {COLOR_SETS, ColorSets} from '@infrastructure/http/color-set/color-sets';
import {COLORED_YARN_SETS, ColoredYarnSets} from '@infrastructure/http/colored-yarn-set/colored-yarn-sets';
import {CREELS, Creels} from '@infrastructure/http/creel/creels';
import {MACHINE_QUALITIES, MachineQualities} from '@infrastructure/http/machine-quality/machine-qualities';
import {ORDERBOOK, Orderbook} from '@infrastructure/http/orderbook/orderbook';
import {HttpStatusBarService} from '@infrastructure/http/status-bar/http-status-bar.service';
import {OrderlineSettingsData} from '@presentation/components/dialog-button/dialog-data/order-lines-settings/order-lines-setting-data.interface';
import {OrderLinesCustomSettingsDialogComponent} from '@presentation/components/dialog-button/dialog-data/order-lines-settings/order-lines-settings.component';
import {OverviewListOrderLine} from '@presentation/pages/texfab/orderbook/overview/overview-list-order-line';
import {OverviewListCreel} from '@presentation/pages/textile-data/creel/overview/overview-list-creel';
import {BackendLimitsConstants} from '@shared/constants/backend-limits.constants';
import {
  AssertionUtils,
  BaseComponent,
  ColDefBuilderFactoryService,
  DialogBuilderFactoryService,
  GridModel,
  GridOptionsBuilderFactoryService,
  LoadingCellOverlayComponent,
  LocalStorageService,
  OverlayComponentParamsAction,
  StatusBar,
  StringUtils,
  TranslateService,
  Unit,
  UuidUtils
} from '@vdw/angular-component-library';
import {AgGridAngular} from 'ag-grid-angular';
import {
  CellMouseOverEvent,
  ColDef,
  ColumnVisibleEvent,
  GetRowIdParams,
  GridApi,
  GridOptions,
  IDetailCellRendererParams,
  IRowNode,
  IServerSideDatasource,
  IServerSideGetRowsParams,
  RowHeightParams,
  RowNode,
  RowSelectedEvent,
  SortModelItem,
  ValueGetterParams
} from 'ag-grid-community';
import {L10nIntlService} from 'angular-l10n';
import {forkJoin, Observable, of, Subject} from 'rxjs';
import {mergeMap, takeUntil} from 'rxjs/operators';
import {AddProductionScheduleNavigationState} from '../../../add-production-schedule-navigation-state.interface';
import {PRODUCTION_SCHEDULE_PLAN, ProductionSchedulePlan} from '../../../plan/production-schedule-plan';
import {OrderLinesHelper} from '../order-lines-helper';
import {ProductionScheduleItemInPathGroupWithIdentification} from '../order-lines-view/production-schedule-item-in-path-group-with-identification';
import {ProductionScheduleOrderLineWithQuantity} from '../order-lines-view/production-schedule-order-line-with-quantity';
import {OrderLinesGroupedDetailCellRendererData} from './detail-cell-renderer/order-lines-grouped-detail-cell-renderer-data.interface';
import {OrderLinesGroupedDetailCellRendererComponent} from './detail-cell-renderer/order-lines-grouped-detail-cell-renderer.component';
import {GroupedOrderLine} from './grouped-order-line';
import {GroupedOrderLinesSelectionService} from './grouped-orderlines-selection.service';

type RowData = ProductionScheduleOrderLineWithQuantity | ProductionScheduleItemInPathGroupWithIdentification;
@Component({
  selector: 'app-drawing-library-order-lines-grouped-view',
  templateUrl: './order-lines-grouped-view.component.html',
  styleUrls: ['./order-lines-grouped-view.component.scss'],
  encapsulation: ViewEncapsulation.None
})
export class OrderLinesGroupedViewComponent extends BaseComponent implements OnInit, OnChanges {
  @Input() public displayTechnicalUnit = true;
  @Input() public productionSchedule: ProductionSchedule;
  @Input() public productionOrderCustomSettings: ProductionOrderCustomSettings;
  @Input() public commercialUnit: Unit;
  @Input() public isPreSelection = false;
  @Input() public selectedPath: ProductionSchedulePath;
  @Input() public selectedRestPathColoredYarnSet: OverviewListColoredYarnSetWithStartDent;
  @Input() public productionSchedulePathsOfColoredYarnSet: ProductionSchedulePathsOfColoredYarnSet;
  @Input() public pathWidths: PathWidth[] = [];
  @Input() public availableSpaceInBuggyInPicks: number;
  @Input() public buggyLengthMaxInPicks: number;
  @Input() public marginInDentsOfMissingDesigns: number;
  @Input() public preselectedOrderlineIds: number[] = [];
  @Input() public addProductionScheduleNavigationState: AddProductionScheduleNavigationState = null;
  @Input() public productionScheduleItemMultiplier: number;
  @Input() public selectedPathContentRowData: ProductionScheduleItemInPathGroup;
  @Output() public selectedOrderlineIdsChanged: EventEmitter<number[]> = new EventEmitter<number[]>();
  @Output() public selectedViewModeChanged = new EventEmitter<ProductionScheduleDrawingViewMode>();
  @Output() public selectedPathOutput: EventEmitter<ProductionSchedulePath> = new EventEmitter<ProductionSchedulePath>();
  @Output() public selectedRestPathColoredYarnSetOutput: EventEmitter<OverviewListColoredYarnSetWithStartDent> = new EventEmitter<OverviewListColoredYarnSetWithStartDent>();

  @ViewChild('container')
  public containerElement: ElementRef;

  @ViewChild('groupedGrid')
  public groupedGrid: AgGridAngular;

  public onlyShowSelected = false;
  public gridModel: GridModel;
  public statusBar: StatusBar;
  public groupedGridOptions: GridOptions;
  public detailGridOptions: GridOptions;
  public loadingForTheFirstTime = true;
  public groupedGridModel: GridModel;
  public detailGridModel: GridModel;
  public groupedOrderlines: GroupedOrderLine[];
  public readonly customSettingsComponent = OrderLinesCustomSettingsDialogComponent;
  public viewModes: ProductionScheduleDrawingViewMode[] = [
    ProductionScheduleDrawingViewMode.ORDER_LINES,
    ProductionScheduleDrawingViewMode.GROUPED_ORDER_LINES,
    ProductionScheduleDrawingViewMode.DESIGNS_LIST,
    ProductionScheduleDrawingViewMode.GROUPED_DESIGNS
  ];

  public selectedViewMode = ProductionScheduleDrawingViewMode.GROUPED_ORDER_LINES;

  private lastFocusedGroupedOrderLineNodeId: string;
  private alternativeQualityNames: string[] = [];
  private alternativeColorSetNames: string[] = [];
  private qualityNames: string[] = [];
  private fullQualities: CarpetWeaveQuality[] = [];
  private colorSetNames: string[] = [];
  private creelColorSetIds: number[] = [];
  private preFilterDataFetched: boolean = false;
  private showOnlyQueryCompatibleOrderlines = true;
  private showPreselectedItems = false;
  private readyToFetchRows: boolean = false;

  private readonly syncWithProductionScheduleSubject = new Subject<void>();
  private readonly WIDTH_HEADER_NAME = 'GENERAL.DIMENSIONS.WIDTH';
  private readonly booleanFalseTranslation = 'GENERAL.CONDITION.NO';
  private readonly booleanTrueTranslation = 'GENERAL.CONDITION.YES';
  private readonly MAX_DETAIL_ROW_HEIGHT = 350;
  private readonly DETAIL_ROW_HEADER_HEIGHT = 36;
  private readonly DETAIL_ROW_VERTICAL_PADDING = 32;
  private readonly DETAIL_ROW_SCROLL_BAR = 18;
  private readonly DETAIL_ROW_HEIGHT = 24;
  private readonly SLICE_SIZE = 0;
  private readonly SLICE_SIZE_DETAILS = 30;

  private readonly noOverlayAction: OverlayComponentParamsAction[] = [
    {
      key: 'addEventRule',
      titleKey: this.translate.instant('PRODUCTION_ORDER.WEAVE_PLAN.PRESELECTION.PRESELECTION'),
      isPrimary: true,
      disabled: false
    }
  ];

  public constructor(
    @Inject(AUTHENTICATION) private authentication: Authentication,
    @Inject(ORDERBOOK) private readonly orderbook: Orderbook,
    @Inject(PRODUCTION_SCHEDULE_PLAN) private readonly productionSchedulePlan: ProductionSchedulePlan,
    @Inject(MACHINE_QUALITIES) private readonly qualities: MachineQualities,
    @Inject(COLORED_YARN_SETS) private readonly coloredYarnSets: ColoredYarnSets,
    @Inject(CREELS) private readonly creelsService: Creels,
    @Inject(COLOR_SETS) private readonly colorSets: ColorSets,
    private readonly gridOptionsBuilderFactoryService: GridOptionsBuilderFactoryService,
    private readonly colDefBuilderFactoryService: ColDefBuilderFactoryService,
    private readonly translate: TranslateService,
    private readonly statusBarService: HttpStatusBarService,
    private readonly orderLinesHelper: OrderLinesHelper,
    private readonly groupedOrderLinesSelectionService: GroupedOrderLinesSelectionService,
    private readonly dialogBuilderFactoryService: DialogBuilderFactoryService,
    private readonly l10nIntlService: L10nIntlService,
    private readonly draggingService: DraggingService,
    private readonly renderer: Renderer2,
    private readonly localStorage: LocalStorageService
  ) {
    super();
    this.orderLinesHelper.displayTechnicalUnit = this.displayTechnicalUnit;
    this.orderLinesHelper.commercialUnit = this.commercialUnit;
  }

  public ngOnInit(): void {
    const currentSubscription = this.authentication.getCurrentSubscription();

    this.filterViewModes(currentSubscription);
    this.initialiseGroupedGridOptions();
    this.groupedOrderLinesSelectionService.getGroupedOrderLineSelected().subscribe((selectedOrderLineIds: number[]) => (this.preselectedOrderlineIds = selectedOrderLineIds));
    this.groupedOrderLinesSelectionService
      .getdetailRowSelected()
      .subscribe((detailComponent: {parentNode: IRowNode; parentSelectionState: boolean; orderlinesSelected: OverviewListOrderLine[]}) =>
        this.onDetailRowsSelectionChanged(detailComponent.parentNode as RowNode, detailComponent.parentSelectionState, detailComponent.orderlinesSelected)
      );

    if (!this.isPreSelection) {
      this.viewModes.push(ProductionScheduleDrawingViewMode.GROUPED_DESIGN_THUMBNAILS);
    }

    this.groupedOrderLinesSelectionService.selectedPath.pipe(takeUntil(this.unSubscribeOnViewDestroy)).subscribe((selectedPath: ProductionSchedulePath) => {
      this.selectedPathOutput.emit(selectedPath);
    });

    if (!AssertionUtils.isEmpty(this.preselectedOrderlineIds) && !this.isPreSelection) {
      this.showPreselectedItems = true;
    }

    this.groupedOrderLinesSelectionService.selectedRestPathColoredYarnSet.pipe(takeUntil(this.unSubscribeOnViewDestroy)).subscribe((coloredYarnSet: OverviewListColoredYarnSetWithStartDent) => {
      this.selectedRestPathColoredYarnSetOutput.emit(coloredYarnSet);
    });

    this.selectedViewMode = this.localStorage.get('drawingLibraryViewMode') || ProductionScheduleDrawingViewMode.GROUPED_ORDER_LINES;

    if (!this.viewModes.includes(this.selectedViewMode)) {
      this.selectedViewMode = ProductionScheduleDrawingViewMode.GROUPED_ORDER_LINES;
    }
    this.selectedViewModeChanged.emit(this.selectedViewMode);
  }

  public ngOnChanges(): void {
    if (!this.isPreSelection) {
      this.setInitializationFilters();
    }

    this.groupedOrderLinesSelectionService.updateDetailCellRendererParams(this.getDetailCellRendererParams());

    if (!AssertionUtils.isEmpty(this.preselectedOrderlineIds) && !this.isPreSelection) {
      this.showPreselectedItems = true;
      this.groupedGrid?.api.redrawRows();
    }
  }

  public getSelectedRows(): number {
    return this.preselectedOrderlineIds ? this.preselectedOrderlineIds.length : 0;
  }

  public toggleSelectedRows(change: boolean): void {
    if (change) {
      this.onlyShowSelected = true;
      this.orderLinesHelper.savedFilterModel = null;
      this.groupedGrid?.api?.setFilterModel(null);

      if (!AssertionUtils.isEmpty(this.preselectedOrderlineIds)) {
        const selectedRowIds = this.preselectedOrderlineIds.map((orderlineId: number) => `${orderlineId}`);

        const filterModel = {
          id: {
            filterModels: [null, {values: selectedRowIds, filterType: 'set'}],
            filterType: 'multi'
          }
        };

        this.orderLinesHelper.savedFilterModel = filterModel;
        this.groupedGrid?.api?.setFilterModel(filterModel);
      }
    } else {
      this.onlyShowSelected = false;
      this.setInitializationFilters();
    }
  }

  public updateGrid(gridModel: GridModel): void {
    this.groupedGrid.api.setFilterModel(gridModel.filterModel);
  }

  public onCustomSettingsChanged(settings: {showPreselectedItems: boolean; showOnlyQueryCompatibleOrderlines: boolean}): void {
    this.showPreselectedItems = settings.showPreselectedItems ?? this.showPreselectedItems;
    this.showOnlyQueryCompatibleOrderlines = settings.showOnlyQueryCompatibleOrderlines ?? this.showOnlyQueryCompatibleOrderlines;
    this.setInitializationFilters();
  }

  public onSelectedViewModeChanged(selectedViewMode: ProductionScheduleDrawingViewMode): void {
    this.localStorage.set('drawingLibraryViewMode', selectedViewMode);

    this.selectedViewModeChanged.emit(selectedViewMode);
  }

  public getCustomSettingsDialogData(): any {
    return {
      showPreselectedItems: this.showPreselectedItems,
      showOnlyQueryCompatibleOrderlines: this.showOnlyQueryCompatibleOrderlines,
      viewMode: this.selectedViewMode
    } as OrderlineSettingsData;
  }

  private filterViewModes(currentSubscription: Subscription): void {
    if (!currentSubscription.hasPermission(Permission.TEXFAB_ORDERBOOK)) {
      this.viewModes = this.viewModes.filter(
        (viewMode: ProductionScheduleDrawingViewMode) => viewMode !== ProductionScheduleDrawingViewMode.ORDER_LINES && viewMode !== ProductionScheduleDrawingViewMode.GROUPED_ORDER_LINES
      );
    }
  }

  public getTooltip(key: string): string {
    const translation = this.translate.instant(key);
    return translation.length > 20 ? translation : '';
  }

  private getDetailCellRendererParams(params?: IDetailCellRendererParams): OrderLinesGroupedDetailCellRendererData {
    return {
      ...(params || ({} as IDetailCellRendererParams)),
      parentState: params?.node?.data?.parentState,
      initialIds: params ? params.node.data.childsSelected?.map((orderLine: OverviewListOrderLine) => orderLine.id) || [] : undefined,
      extraData: {
        displayTechnicalUnit: this.displayTechnicalUnit,
        commercialUnit: this.commercialUnit,
        productionSchedule: this.productionSchedule,
        isPreSelection: this.isPreSelection,
        productionOrderCustomSettings: this.productionOrderCustomSettings,
        selectedPath: this.selectedPath,
        selectedRestPathColoredYarnSet: this.selectedRestPathColoredYarnSet,
        productionSchedulePathsOfColoredYarnSet: this.productionSchedulePathsOfColoredYarnSet,
        availableSpaceInBuggyInPicks: this.availableSpaceInBuggyInPicks,
        buggyLengthMaxInPicks: this.buggyLengthMaxInPicks,
        pathWidths: this.pathWidths,
        fullQualities: this.fullQualities,
        marginInDentsOfMissingDesigns: this.marginInDentsOfMissingDesigns,
        productionScheduleItemMultiplier: this.productionScheduleItemMultiplier
      }
    };
  }

  private onCellMouseOver(event: CellMouseOverEvent): void {
    this.lastFocusedGroupedOrderLineNodeId = event.node.id;
  }

  private onDetailRowsSelectionChanged(node: RowNode, parentSelectionState: boolean, orderlinesSelected: OverviewListOrderLine[]): void {
    const deselectedIds = node.data.orderlineIds.filter((id: number) => !orderlinesSelected.some((des: OverviewListOrderLine) => des.id === id));

    let currentSelectedOrderLineIds = this.preselectedOrderlineIds?.filter((orderLineId: number) => !deselectedIds.map(Number).includes(orderLineId)) ?? [];

    const orderLinesSet = new Set([
      ...currentSelectedOrderLineIds,
      ...orderlinesSelected
        .filter((newOrderLine: OverviewListOrderLine) => currentSelectedOrderLineIds.findIndex((orderlineId: number) => orderlineId === newOrderLine.id) === -1)
        .map((newOrderLine: OverviewListOrderLine) => newOrderLine.id)
    ]);
    currentSelectedOrderLineIds = [...orderLinesSet];

    this.groupedOrderLinesSelectionService.setGroupedOrderLinesSelected(currentSelectedOrderLineIds);

    node.data.childsSelected = orderlinesSelected;
    node.data.parentState = parentSelectionState;
    node.data.updatingFromDetails = true;
    node.selectThisNode(parentSelectionState);

    setTimeout(() => (node.data.updatingFromDetails = false), 100);

    this.selectedOrderlineIdsChanged.emit(this.preselectedOrderlineIds);
  }

  private parentNodeSelected(event: RowSelectedEvent): void {
    if (event.node.data.updatingFromDetails) {
      return;
    }

    if (this.lastFocusedGroupedOrderLineNodeId !== event.node.id) {
      const selectedOrderLines = this.preselectedOrderlineIds.filter((orderlineId: number) => event.node.data.orderlineIds.map(Number).includes(orderlineId));
      const selectionState = this.groupedOrderLinesSelectionService.getParentSelectionState(event.node.data.orderlineIds, selectedOrderLines);

      (event.node as RowNode).selectThisNode(selectionState);
      return;
    }

    const detailGridId = 'detail_' + event.node.id;
    const detailGrid = event.api.getDetailGridInfo(detailGridId);

    if (detailGrid) {
      detailGrid.api.forEachNode((detailNode: RowNode) => detailNode.setSelected(event.node.isSelected()));
      return;
    }

    event.node.data.parentState = event.node.isSelected();

    this.getAndSelectOrderLines(event);
  }

  private getAllPreFilterData(): void {
    let qualityObservable: Observable<WeaveQuality> = of(null);
    let coloredYarnSetObservables: Observable<ColoredYarnSet>[] = [];

    if (!AssertionUtils.isNullOrUndefined(this.addProductionScheduleNavigationState?.machineQualityId) && !AssertionUtils.isNullOrUndefined(this.addProductionScheduleNavigationState?.creelId)) {
      qualityObservable = this.getQualityById(this.addProductionScheduleNavigationState.machineQualityId);
      this.creelsService.getById(this.addProductionScheduleNavigationState.creelId).subscribe((overviewListCreel: OverviewListCreel) => {
        coloredYarnSetObservables = overviewListCreel.coloredYarnSetsWithStartDent.map((coloredYarnSetsWithStartDent: OverviewListColoredYarnSetWithStartDent) => {
          return this.getColoredYarnSetById(coloredYarnSetsWithStartDent.coloredYarnSet.id);
        });

        this.processObservables(qualityObservable, coloredYarnSetObservables);
      });
    } else if (!AssertionUtils.isNullOrUndefined(this.productionSchedule?.machineQuality)) {
      qualityObservable = this.getQualityById(this.productionSchedule.machineQuality.id);

      coloredYarnSetObservables = this.productionSchedule.coloredYarnSets.map((coloredYarnSet: OverviewListColoredYarnSetWithStartDent) => {
        return this.getColoredYarnSetById(coloredYarnSet.coloredYarnSet.id);
      });

      this.processObservables(qualityObservable, coloredYarnSetObservables);
    } else {
      this.preFilterDataFetched = true;
      this.setInitializationFilters();
    }
  }

  private processObservables(qualityObservable: Observable<WeaveQuality>, coloredYarnSetObservables: Observable<ColoredYarnSet>[]): void {
    forkJoin([qualityObservable, ...coloredYarnSetObservables])
      .pipe(
        mergeMap(([quality, ...coloredYarnSets]: [CarpetWeaveQuality, ColoredYarnSet]) => {
          this.qualityNames.push(quality?.technicalName);
          this.fullQualities.push(quality);
          this.alternativeQualityNames.push(quality?.technicalName);

          coloredYarnSets.forEach((coloredYarnSet: ColoredYarnSet) => {
            this.colorSetNames.push(coloredYarnSet?.colorSet?.name);
            this.alternativeColorSetNames.push(coloredYarnSet?.colorSet?.name);
            this.creelColorSetIds.push(coloredYarnSet?.colorSet?.id);
          });

          let alternativeQualityObservables = [];
          let alternativeColorSetObservables = [];

          if (quality.alternativeMachineQualityIds?.length > 0) {
            alternativeQualityObservables = quality?.alternativeMachineQualityIds?.map((alternativeQualityId: number) => {
              return this.getQualityById(alternativeQualityId);
            });
          }

          coloredYarnSets?.map((coloredYarnSet: ColoredYarnSet) => {
            if (coloredYarnSet.colorSet?.alternativeColorSetIds?.length > 0) {
              alternativeColorSetObservables = coloredYarnSet.colorSet.alternativeColorSetIds.map((alternativeColorSetId: number) => {
                return this.getColorSetById(alternativeColorSetId);
              });
            }
          });

          if (AssertionUtils.isEmpty(alternativeQualityObservables) && AssertionUtils.isEmpty(alternativeColorSetObservables)) {
            return of([]);
          }

          return forkJoin(alternativeQualityObservables.concat(alternativeColorSetObservables) as any);
        })
      )
      .subscribe((alternatives: (CarpetWeaveQuality | ColorSet)[]) => {
        alternatives.forEach((alternative: CarpetWeaveQuality | ColorSet) => {
          if (alternative instanceof CarpetWeaveQuality) {
            this.qualityNames.push(alternative.technicalName);
            this.fullQualities.push(alternative);
          } else if (alternative instanceof ColorSet) {
            this.alternativeColorSetNames.push(alternative.name);
          }
        });

        this.preFilterDataFetched = true;
        this.setInitializationFilters();
      });
  }

  private getColorSetById(id: number): Observable<ColorSet> {
    return this.colorSets.getById(id);
  }

  private getQualityById(id: number): Observable<WeaveQuality> {
    return this.qualities.getById(id);
  }

  private getColoredYarnSetById(id: number): Observable<ColoredYarnSet> {
    return this.coloredYarnSets.getById(id);
  }

  private getRowHeightForDetailRow(params: RowHeightParams): number {
    this.groupedGridOptions.detailRowAutoHeight = false;
    const isDetailRow = params.node.detail;

    if (!isDetailRow) {
      return undefined;
    }

    if (isDetailRow && params?.data?.orderlineIds?.length > 8) {
      return this.MAX_DETAIL_ROW_HEIGHT;
    } else {
      return this.DETAIL_ROW_HEADER_HEIGHT + 2 * this.DETAIL_ROW_VERTICAL_PADDING + this.DETAIL_ROW_SCROLL_BAR + params.data.orderlineIds.length * this.DETAIL_ROW_HEIGHT;
    }
  }

  private setInitializationFilters(): void {
    if (!this.preFilterDataFetched) {
      return;
    }

    let filterModel: any;

    filterModel = this.orderLinesHelper.getInitializationFilterModel(
      this.showPreselectedItems,
      this.productionSchedule,
      this.selectedPath,
      this.selectedRestPathColoredYarnSet,
      this.preselectedOrderlineIds,
      this.addProductionScheduleNavigationState,
      this.productionOrderCustomSettings,
      this.alternativeQualityNames,
      this.qualityNames,
      this.alternativeColorSetNames,
      this.colorSetNames,
      this.showOnlyQueryCompatibleOrderlines,
      this.isPreSelection,
      this.selectedPathContentRowData
    );

    this.readyToFetchRows = true;
    this.groupedGrid?.api?.setFilterModel(filterModel);

    //because we fetched no rows yet, we need to explictly trigger a new getrows on aggrid
    this.groupedGrid?.api?.setGridOption('serverSideDatasource', this.getGroupedServerSideDataSource());
  }

  private getGroupedServerSideDataSource(): IServerSideDatasource {
    if (!this.readyToFetchRows) {
      return {
        getRows: (params: IServerSideGetRowsParams): void => {
          params.success({
            rowData: [],
            rowCount: 0
          });
        }
      };
    } else {
      return {
        getRows: (params: IServerSideGetRowsParams): void => {
          const sortModel: SortModelItem[] = params.request.sortModel;
          const startRow: number = params.request.startRow;
          const endRow: number = params.request.endRow;
          const filterModel = params.request.filterModel;
          this.orderLinesHelper.savedFilterModel = filterModel;

          this.groupedGridModel = new GridModel(startRow, endRow, sortModel, filterModel);

          const groupedBy = this.getGroupedByColumns(params);

          this.orderbook
            .getGroupedOrderlines(this.groupedGridModel, groupedBy)
            .pipe(takeUntil(this.unSubscribeOnViewDestroy))
            .subscribe({
              next: (groupedOrderLines: GroupedOrderLine[]) => {
                this.groupedOrderlines = groupedOrderLines;

                if (startRow === 0) {
                  this.getStatusBarData(this.groupedGridModel);

                  if (groupedOrderLines.length === 0) {
                    params.api.showNoRowsOverlay();
                  } else {
                    params.api.hideOverlay();
                  }
                }

                setTimeout(() => {
                  params.success({
                    rowData: groupedOrderLines,
                    rowCount: groupedOrderLines.length
                  });

                  this.setSelectedOrderlines(this.groupedGrid.api);
                });
              },
              error: (error: any) => this.orderLinesHelper.onServerSideDataSourceError(params, error)
            });
        }
      };
    }
  }

  private getGroupedByColumns(params: IServerSideGetRowsParams): string[] {
    const groupedBy = [];

    if (params.api.getColumn('machineQualityName').isVisible()) {
      groupedBy.push('QUALITY');
    }
    if (params.api.getColumn('colorSetName').isVisible()) {
      groupedBy.push('COLOR_SET');
    }
    if (params.api.getColumn('commercialWidthInCM').isVisible()) {
      groupedBy.push('COMMERCIAL_WIDTH_IN_CM');
    }
    if (params.api.getColumn('technicalWidthInDents').isVisible()) {
      groupedBy.push('TECHNICAL_WIDTH_IN_DENTS');
    }

    if (AssertionUtils.isEmpty(groupedBy)) {
      groupedBy.push('QUALITY');
      groupedBy.push('COLOR_SET');
    }

    return groupedBy;
  }

  private onColumnVisibleChange(event: any): void {
    this.groupedGrid.api.refreshServerSide();
  }

  private setSelectedOrderlines(gridApi: GridApi): void {
    if (this.preselectedOrderlineIds?.length > 0) {
      gridApi?.forEachNode((node: RowNode) => {
        if (!AssertionUtils.isNullOrUndefined(node.data)) {
          const selectedOrderLines = this.preselectedOrderlineIds.filter((orderlineId: number) => node.data.orderlineIds.map(Number).includes(orderlineId));
          const selectionState = this.groupedOrderLinesSelectionService.getParentSelectionState(node.data.orderlineIds, selectedOrderLines);

          node.data.childsSelected = selectedOrderLines;
          node.data.parentState = selectionState;
          node.selectThisNode(selectionState);
        }
      });
    }
  }

  private getAndSelectOrderLines(event: RowSelectedEvent): void {
    const orderlineIds = event.node.data.orderlineIds;
    const sortModel: SortModelItem[] = [];
    let filterModel = {};

    if (!AssertionUtils.isNullOrUndefined(orderlineIds) && !AssertionUtils.isEmpty(orderlineIds)) {
      filterModel = {
        id: {
          filterModels: [null, {values: orderlineIds, filterType: 'set'}],
          filterType: 'multi'
        }
      };
    }

    this.detailGridModel = new GridModel(0, this.SLICE_SIZE_DETAILS, sortModel, filterModel);

    this.orderbook
      .getSlice(OrderLinesTarget.PRODUCTION_SCHEDULE, this.detailGridModel, this.productionSchedule?.id?.toString())
      .pipe(takeUntil(this.unSubscribeOnViewDestroy))
      .subscribe((orderLines: OverviewListOrderLine[]) => {
        let selectedOrderlines: number[] = [];

        if (event.node.isSelected()) {
          event.node.data.childsSelected = orderLines;
        } else if (!AssertionUtils.isNullOrUndefined(event.node.isSelected()) && !event.node.isSelected()) {
          event.node.data.childsSelected = [];
        }

        if (event.node.isSelected()) {
          const orderLinesSet = new Set([
            ...this.preselectedOrderlineIds,
            ...orderLines
              .filter((newOrderLine: OverviewListOrderLine) => this.preselectedOrderlineIds.findIndex((orderlineId: number) => orderlineId === newOrderLine.id) === -1)
              .map((newOrderLine: OverviewListOrderLine) => newOrderLine.id)
          ]);
          selectedOrderlines = [...orderLinesSet];
        } else {
          selectedOrderlines = this.preselectedOrderlineIds?.filter((orderlineId: number) => !event.data.orderlineIds.includes(orderlineId));
        }

        this.groupedOrderLinesSelectionService.setGroupedOrderLinesSelected(selectedOrderlines);
        this.selectedOrderlineIdsChanged.emit(this.preselectedOrderlineIds);
      });
  }

  private getStatusBarData(gridModel: GridModel): void {
    gridModel.startRow = 0;
    gridModel.endRow = BackendLimitsConstants.INT32_MAX;
    this.statusBarService
      .getStatusBarData(StatusBarTarget.ORDER_LINES, gridModel)
      .pipe(takeUntil(this.unSubscribeOnViewDestroy))
      .subscribe((statusBar: StatusBar) => {
        statusBar.totalGroupedRows = this.groupedOrderlines.length;
        this.statusBar = statusBar;
      });
  }

  private getPossibleColumnsValues(columnIdentifier: string): Observable<string[]> {
    return this.orderbook.getPossibleValues(columnIdentifier).pipe(takeUntil(this.unSubscribeOnViewDestroy));
  }

  private getDropdownCellValue(params: ValueGetterParams): string {
    return `${params?.data?.quality?.name} (${params?.data?.orderlineIds?.length})`;
  }

  private initialiseGroupedGridOptions(): void {
    const orderLinesGroupedDetailCellRendererComponent = new OrderLinesGroupedDetailCellRendererComponent(
      this.authentication,
      this.orderbook,
      this.productionSchedulePlan,
      this.colDefBuilderFactoryService,
      this.orderLinesHelper,
      this.gridOptionsBuilderFactoryService,
      this.groupedOrderLinesSelectionService,
      this.translate,
      this.dialogBuilderFactoryService,
      this.l10nIntlService,
      this.draggingService,
      this.renderer
    );

    const defaultGroupedGridOptions = this.gridOptionsBuilderFactoryService
      .getBuilder(this.getGroupColumnDefinitions(), GridIdentifier.GROUPED_ORDER_ITEMS)
      .withHeaderHeight(32)
      .withFloatingFiltersHeight(32)
      .withServerSideDataSource(this.SLICE_SIZE, this.getGroupedServerSideDataSource())
      .withMasterDetail(OrderLinesGroupedDetailCellRendererComponent, true, (params: any) => this.getDetailCellRendererParams(params))
      .withLoadingCellRenderer('customLoadingCellRenderer')
      .withGetRowId((params: GetRowIdParams) => params.data.quality.id)
      .withComponents({
        customLoadingCellRenderer: LoadingCellOverlayComponent,
        OrderLinesGroupedDetailCellRendererComponent: orderLinesGroupedDetailCellRendererComponent
      })
      .withNoRowsOverlay(this.isPreSelection ? this.orderLinesHelper.getNoRowsOverlayForPreselection(this.groupedGrid) : this.orderLinesHelper.getNoRowsOverlayForBuilder(this.noOverlayAction))
      .withRowSelection(this.isPreSelection, false)
      .withOnFirstDataRendered(() => {
        this.syncWithProductionScheduleSubject.next();
      })
      .withStopEditingWhenCellsLoseFocus()
      .withOnGridReady(() => this.getAllPreFilterData())
      .withOnCellMouseOver((event: CellMouseOverEvent) => this.onCellMouseOver(event))
      .withGetRowHeight((params: RowHeightParams) => this.getRowHeightForDetailRow(params))
      .withGetRowId((params: GetRowIdParams) => UuidUtils.generateV4Uuid())
      .withOnColumnVisible((event: ColumnVisibleEvent) => this.onColumnVisibleChange(event))
      .withCompactView();
    if (this.isPreSelection) {
      this.groupedGridOptions = defaultGroupedGridOptions.withOnRowSelected((event: RowSelectedEvent) => this.parentNodeSelected(event)).build();
    } else {
      this.groupedGridOptions = defaultGroupedGridOptions.build();
    }
  }

  private getGroupColumnDefinitions(): ColDef[] {
    return [
      this.colDefBuilderFactoryService.getBuilder().withColIdAndField('id').withNumericMultiFilter(this.getPossibleColumnsValues('id')).withHide().withHiddenInToolPanel().build(),
      this.colDefBuilderFactoryService
        .getBuilder()
        .withColId('machineQualityName')
        .withField('quality.name')
        .withCellRenderer('agGroupCellRenderer')
        .withHeaderName('TEXTILE_DATA.MACHINE_QUALITY.MACHINE_QUALITY')
        .withValueGetter((params: ValueGetterParams) => this.getDropdownCellValue(params))
        .withTooltipField('article.machineQuality.name')
        .withComparator(StringUtils.stringComparator)
        .withTextMultiFilter(this.getPossibleColumnsValues('machineQualityName'))
        .withSuppressMovable()
        .build(),
      this.colDefBuilderFactoryService
        .getBuilder()
        .withColId('colorSetName')
        .withField('colorSet.name', true)
        .withHeaderName('TEXTILE_DATA.COLOR_SET.COLOR_SET')
        .withComparator(StringUtils.stringComparator)
        .withCellRendererSelector(this.orderLinesHelper.cellRendererSelectorForOrderLinesOnly())
        .withTextMultiFilter(this.getPossibleColumnsValues('colorSetName'))
        .build(),
      this.colDefBuilderFactoryService
        .getBuilder()
        .withColId('commercialWidthInCM')
        .withField('commercialWidthInCm', true)
        .withHeaderName(this.WIDTH_HEADER_NAME, 1, false, Unit.CENTIMETER)
        .withRightAlignment()
        .withCellRendererSelector(this.orderLinesHelper.cellRendererSelectorForOrderLinesOnly())
        .withNumericMultiFilter(this.getPossibleColumnsValues('commercialWidthInCM'))
        .build(),
      this.colDefBuilderFactoryService
        .getBuilder()
        .withColIdAndField('technicalWidthInDents', true)
        .withHeaderName(this.WIDTH_HEADER_NAME, 1, false, Unit.DENTS)
        .withRightAlignment()
        .withNumericMultiFilter(this.getPossibleColumnsValues('technicalWidthInDents'))
        .build(),
      this.colDefBuilderFactoryService
        .getBuilder()
        .withColIdAndField('lengthInMM')
        .withHeaderName('ORDERBOOK.TO_PLAN_LENGTH', 1, false, Unit.METER)
        .withRightAlignment()
        .withoutFilter()
        .withSortable()
        .withMMConversion(Unit.METER, false, 2)
        .build(),
      this.colDefBuilderFactoryService
        .getBuilder()
        .withColIdAndField('lengthInPicks', true)
        .withHeaderName('ORDERBOOK.TO_PLAN_LENGTH', 1, false, Unit.PICKS)
        .withRightAlignment()
        .withoutFilter()
        .withSortable()
        .build(),
      this.colDefBuilderFactoryService
        .getBuilder()
        .withColIdAndField('surfaceInMM2')
        .withHeaderName('ORDERBOOK.TO_PLAN_SURFACE', 1, false, Unit.SQUARE_METER)
        .withRightAlignment()
        .withoutFilter()
        .withSortable()
        .withSquareMMConversion(Unit.SQUARE_METER, 2)
        .build(),
      this.colDefBuilderFactoryService.getBuilder().withColIdAndField('numberOfPieces', true).withHeaderName('ORDERBOOK.NUMBER_OF_PIECES').withRightAlignment().withoutFilter().withSortable().build(),
      this.colDefBuilderFactoryService
        .getBuilder()
        .withColIdAndField('missingDesign')
        .withHeaderName('ORDERBOOK.MISSING_DESIGN')
        .withValueGetter((params: ValueGetterParams) => (params.data.missingDesign ? this.translate.instant(this.booleanTrueTranslation) : this.translate.instant(this.booleanFalseTranslation)), true)
        .withBooleanFilter(this.translate.instant(this.booleanFalseTranslation), this.translate.instant(this.booleanTrueTranslation))
        .withSortable(false)
        .withoutFilter()
        .build(),
      this.colDefBuilderFactoryService
        .getBuilder()
        .withColIdAndField('amountLeft')
        .withValueGetter((params: ValueGetterParams<RowData>): number => this.orderLinesHelper.getAmountLeft(params.data, this.productionSchedule))
        .withHeaderName('ORDERBOOK.TO_PLAN_AMOUNT')
        .withSortable(false)
        .withRightAlignment()
        .withCellRendererSelector(this.orderLinesHelper.cellRendererSelectorForOrderLinesOnly())
        .withNumericMultiFilter(this.getPossibleColumnsValues('amountLeft'))
        .withHiddenInToolPanel()
        .withHide()
        .build(),
      this.colDefBuilderFactoryService
        .getBuilder()
        .withColId('compatibleColorSetId')
        .withNumericMultiFilter(this.getPossibleColumnsValues('colorSetId'))
        .withHide()
        .withHiddenInToolPanel()
        .withField('colorSet.id')
        .build()
    ];
  }
}
